import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Zap, Crown, Palette, ExternalLink } from 'lucide-react';
import { getSubscriptionTiers, createSubscriptionCheckout } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// Helper function for subscription button styling
const getButtonClassName = (requiresApplication, isPopular) => {
  if (requiresApplication) {
    return 'w-full py-3 px-6 rounded-lg font-medium bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2';
  }
  if (isPopular) {
    return 'btn-hotrod w-full disabled:opacity-50';
  }
  return 'btn-outline w-full disabled:opacity-50';
};

function PricingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    async function fetchTiers() {
      try {
        const res = await getSubscriptionTiers();
        setTiers(res.data);
      } catch (error) {
        console.error('Error fetching tiers:', error);
        toast.error('Failed to load pricing');
      } finally {
        setLoading(false);
      }
    }
    fetchTiers();
  }, []);

  const handleSubscribe = async (tier) => {
    if (!isAuthenticated) {
      navigate('/signup');
      return;
    }

    // Handle creator application redirect
    if (tier === 'creator') {
      navigate('/creator-application');
      return;
    }

    if (tier === 'free') {
      toast.success('You are on the Free plan');
      return;
    }

    setProcessing(tier);
    try {
      const res = await createSubscriptionCheckout(tier);
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setProcessing(null);
    }
  };

  const getIcon = (tier) => {
    switch (tier) {
      case 'free': return Zap;
      case 'premium': return Crown;
      case 'creator': return Palette;
      default: return Zap;
    }
  };

  const getButtonText = (tier, isCurrentPlan, isProcessing, requiresApplication) => {
    if (isCurrentPlan) return 'Current Plan';
    if (isProcessing) return 'Processing...';
    if (requiresApplication) return 'Apply Now';
    if (tier.price === 0) return 'Get Started';
    if (tier.trial_days > 0) return 'Start Free Trial';
    return 'Subscribe Now';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-hot-rod border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 pt-16 page-padding" data-testid="pricing-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-7xl font-teko text-gradient mb-4">Choose Your Plan</h1>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            From casual reading to professional creating. Pick the plan that fits your journey.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 items-stretch">
          {tiers.map((tier) => {
            const Icon = getIcon(tier.tier);
            const isCurrentPlan = user?.subscription_tier === tier.tier;
            const isPopular = tier.popular;
            const requiresApplication = tier.requires_application;
            
            return (
              <div 
                key={tier.tier}
                className={'relative h-full ' + (isPopular ? 'price-card-featured' : '')}
                data-testid={'tier-' + tier.tier}
              >
                <div className={'card-hotrod p-8 h-full flex flex-col ' + (isPopular ? 'bg-surface' : '')}>
                  {isPopular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="badge-new px-4 py-1">Most Popular</span>
                    </div>
                  )}
                  
                  {requiresApplication && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm px-4 py-1 rounded-full font-medium">
                        Application Required
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3 mb-4">
                    <Icon className={isPopular ? 'text-hot-rod' : requiresApplication ? 'text-purple-400' : 'text-chrome-orange'} size={32} />
                    <h2 className="font-teko text-3xl uppercase">{tier.name}</h2>
                  </div>
                  
                  <div className="mb-6">
                    {requiresApplication ? (
                      <div>
                        <span className="stat-number text-4xl font-bold text-purple-400">Apply</span>
                        <p className="text-sm text-text-secondary mt-1">Free once approved</p>
                      </div>
                    ) : tier.trial_days > 0 ? (
                      <div>
                        {/* FREE TRIAL - Most prominent */}
                        <div className="mb-3">
                          <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xl font-bold rounded-full shadow-lg shadow-green-500/30">
                            <Zap size={20} />
                            {tier.trial_days} DAYS FREE
                          </span>
                        </div>
                        <p className="text-sm text-text-secondary">
                          Then ${tier.price.toFixed(2)}/{tier.billing_interval_count > 1 ? tier.billing_interval_count + ' ' : ''}{tier.billing_interval || 'month'}{tier.billing_interval_count > 1 ? 's' : ''}
                        </p>
                        <p className="text-xs text-text-secondary mt-1">Cancel anytime during trial</p>
                      </div>
                    ) : (
                      <>
                        <span className="stat-number text-5xl font-bold">
                          {tier.price === 0 ? 'Free' : '$' + tier.price.toFixed(2)}
                        </span>
                        {tier.price > 0 && (
                          <span className="text-text-secondary">
                            /{tier.billing_interval_count > 1 ? tier.billing_interval_count + ' ' : ''}{tier.billing_interval || 'month'}{tier.billing_interval_count > 1 ? 's' : ''}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  
                  <ul className="space-y-3 flex-1 mb-8">
                    {tier.features.map((feature, i) => (
                      <li key={`${tier.tier}-feature-${i}`} className="flex items-start gap-3">
                        <Check className={isPopular ? 'text-hot-rod' : requiresApplication ? 'text-purple-400' : 'text-chrome-orange'} size={20} />
                        <span className="text-text-secondary">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button
                    onClick={() => handleSubscribe(tier.tier)}
                    disabled={processing === tier.tier || isCurrentPlan}
                    className={getButtonClassName(requiresApplication, isPopular)}
                    data-testid={'subscribe-' + tier.tier}
                  >
                    <span>{getButtonText(tier, isCurrentPlan, processing === tier.tier, requiresApplication)}</span>
                    {requiresApplication && !isCurrentPlan && <ExternalLink size={16} />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="mt-16 grid md:grid-cols-2 gap-8">
          <div className="card-hotrod p-6">
            <h3 className="font-teko text-2xl mb-4 text-chrome-orange">Premium Benefits</h3>
            <p className="text-text-secondary">
              Premium members get <strong className="text-white">unlimited access</strong> to all chapters across every comic.
              No more unlock fees — just endless reading!
            </p>
          </div>
          
          <div className="card-hotrod p-6">
            <h3 className="font-teko text-2xl mb-4 text-purple-400">Become a Creator</h3>
            <p className="text-text-secondary">
              Ready to share your stories? <strong className="text-white">Apply to become a creator</strong> and unlock 
              publishing tools, analytics, and revenue sharing.
            </p>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16">
          <h2 className="font-teko text-4xl text-center mb-8">Frequently Asked Questions</h2>
          
          <div className="space-y-4 max-w-3xl mx-auto">
            <div className="card-hotrod p-6">
              <h4 className="font-teko text-xl mb-2">What does Premium unlock?</h4>
              <p className="text-text-secondary">
                Premium gives you access to ALL chapters on the platform — including paid/locked content. 
                No chapter-by-chapter fees, ever.
              </p>
            </div>
            
            <div className="card-hotrod p-6">
              <h4 className="font-teko text-xl mb-2">How do I become a Creator?</h4>
              <p className="text-text-secondary">
                Click &ldquo;Apply Now&rdquo; on the Creator plan. Fill out a quick application with your portfolio or sample work.
                Our team reviews applications within 48 hours.
              </p>
            </div>
            
            <div className="card-hotrod p-6">
              <h4 className="font-teko text-xl mb-2">Can I upgrade or downgrade anytime?</h4>
              <p className="text-text-secondary">Yes! You can change your plan at any time. Changes take effect on your next billing cycle.</p>
            </div>
            
            <div className="card-hotrod p-6">
              <h4 className="font-teko text-xl mb-2">How do Creators earn money?</h4>
              <p className="text-text-secondary">
                Creators earn through tips, paid chapter unlocks (from non-Premium readers), 
                ad revenue sharing, and subscriber donations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PricingPage;
